import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import MobileTopBar from '../components/MobileTopBar';
import { Briefcase, MapPin, Users, Loader2, RefreshCcw } from 'lucide-react';
import { frappeApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

function JobCard({ job }) {
  return (
    <div className="rounded-2xl bg-white border border-[#ECE6FA] p-5 hover:border-[#C7BFF5] hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#F1ECFE] flex items-center justify-center shrink-0">
          <Briefcase size={18} className="text-[#5B4FE8]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-bold text-[#1A1533]">{job.title}</h3>
          <p className="text-[12.5px] text-[#4A4463] mt-1">{job.department || '—'}</p>
          <div className="mt-3 flex items-center gap-3 text-[12px] text-[#4A4463] flex-wrap">
            {job.location && (
              <span className="inline-flex items-center gap-1"><MapPin size={12} />{job.location}</span>
            )}
            <span className="inline-flex items-center gap-1"><Users size={12} />{job.openings} opening{job.openings !== 1 ? 's' : ''}</span>
            <span className="inline-block px-2 py-0.5 rounded-full bg-[#E8F7EE] text-[#1F8A4E] text-[11px] font-semibold">{job.status}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OpenJobs() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [isLive, setIsLive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { jobs: list, live } = await frappeApi.jobs();
      setJobs(list || []);
      setIsLive(!!live);
    } catch (e) {
      toast({ title: 'Failed to load jobs', description: e?.message || 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      await frappeApi.sync();
      await load();
      toast({ title: 'Synced', description: isLive ? 'Synced with Frappe HR' : 'Using mock jobs (Frappe not configured)' });
    } catch (e) {
      toast({ title: 'Sync failed', description: e?.message || 'Network error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5FF]">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <MobileTopBar title="Open jobs" />
        <div className="px-5 md:px-10 pt-6 md:pt-8 pb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-[22px] md:text-[28px] font-bold text-[#1A1533] tracking-tight">Open jobs</h1>
            {!isLive && (
              <p className="text-[12.5px] text-[#B7721B] mt-1">
                Showing sample jobs. Configure Frappe HR in Settings → API access to sync real jobs.
              </p>
            )}
          </div>
          <button
            onClick={sync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5DEF9] text-[#1A1533] text-[13px] font-semibold hover:bg-[#F1ECFE] transition-colors disabled:opacity-60"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} className="text-[#5B4FE8]" />}
            Sync
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 size={28} className="animate-spin text-[#5B4FE8]" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-5 md:px-10 pb-10">
            {jobs.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        )}
      </main>
    </div>
  );
}
